import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Modal } from '@modernhealth/vital-components';
import { useAnalytics } from '@modernhealth/web-analytics';

import { ExternalLinkButton } from 'src/shared/analytics/components';

import { ReactComponent as GoogleCalendarIcon } from '../assets/social-google.svg';
import { ReactComponent as OutlookIcon } from '../assets/social-outlook.svg';

export enum Calendar {
    GOOGLE_CALENDAR = 'google-calendar',
    OUTLOOK = 'outlook',
    ICS = 'ics',
}

interface CalendarProductConfig {
    iconComponent: React.ComponentType;
    productName: string; // left untranslated
}

interface CalendarGenericConfig {
    iconComponent: React.ComponentType;
    labelI18nKey: string;
    labelSuffix?: string; // left untranslated
}

const CalendarConfig: Record<Calendar, CalendarProductConfig | CalendarGenericConfig> = {
    [Calendar.GOOGLE_CALENDAR]: {
        iconComponent: GoogleCalendarIcon,
        productName: 'Google Calendar',
    },
    [Calendar.ICS]: {
        iconComponent: () => <Icon size={18} name="calendar" />,
        labelI18nKey: 'videoSessions:session_confirmation_modal.add_to_calendar',
        labelSuffix: ' (.ics)',
    },
    [Calendar.OUTLOOK]: {
        iconComponent: OutlookIcon,
        productName: 'Microsoft Outlook',
    },
};

export interface AddToCalendarModalProps {
    visible: boolean;
    onClose: () => void;
    calendarLinks: Partial<Record<Calendar, string | null>> | null;
    calendars: Calendar[];
}

export const AddToCalendarModal: React.FC<AddToCalendarModalProps> = ({
    visible,
    calendars,
    calendarLinks,
    onClose,
}) => {
    const { t } = useTranslation();
    const analytics = useAnalytics();

    return (
        <Modal
            maxWidth={500}
            isVisible={visible}
            onCloseButtonClick={() => {
                analytics.trackClick('add_to_calendar_modal.close');
                onClose();
            }}
            title={t('videoSessions:session_confirmation_modal.add_to_calendar_modal.title')}
        >
            <div
                css={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 20,
                }}
            >
                {calendars.map(calendar => {
                    if (!calendarLinks) return null;

                    const config = CalendarConfig[calendar];
                    if (!config) {
                        return null;
                    }

                    const buttonLabel =
                        'productName' in config
                            ? t(
                                  'videoSessions:session_confirmation_modal.add_to_calendar_product',
                                  {
                                      calendar: config.productName,
                                  },
                              )
                            : `${t(config.labelI18nKey)}${config.labelSuffix ?? ''}`;

                    const Icon = config.iconComponent;

                    const href = calendarLinks[calendar];

                    if (!href) return null;

                    return (
                        <ExternalLinkButton
                            key={calendar}
                            variant="secondary"
                            rounded
                            fluid
                            size="large"
                            href={href}
                            eventName="add_to_calendar_modal.add_to_calendar"
                            eventProperties={{
                                calendar_type: calendar,
                            }}
                            onClick={() => onClose()}
                        >
                            <Icon
                                aria-hidden
                                css={{
                                    width: 18,
                                    height: 18,
                                    marginRight: 12,
                                    transform: 'translateY(-1px)',
                                }}
                            />
                            {buttonLabel}
                        </ExternalLinkButton>
                    );
                })}
            </div>
        </Modal>
    );
};
